// Course progress sync — bridges localStorage to the customer metafield
// via the Shopify app proxy. Loaded by sections/course.liquid.
//
// localStorage shape (one entry per SKU, written by sections/course.liquid):
//   key:   courseProgress_<SKU>           e.g. courseProgress_SD-CPS30
//   value: { completedLessons: ["1","2"], lessonTotal: "7", path: "/products/..." }
//
// Server endpoint lives in cloudflare/course-progress/worker.js. The worker
// validates the proxy HMAC signature + logged_in_customer_id Shopify adds,
// then merges completedLessons server-side (set-union), so concurrent writes
// from different tabs/devices won't clobber each other.

(function () {
  const PROXY_BASE = '/apps/course-progress';
  const LOCAL_STORAGE_PREFIX = 'courseProgress_';

  // Note on auth: the worker requires the Shopify app proxy to have signed
  // the request with `logged_in_customer_id`, which only happens for a
  // logged-in storefront customer session. We don't gate the call client-side;
  // if there's no session, the worker returns 401 and postProgress logs+skips.

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  // Call from the course player when a lesson is marked done.
  async function markLessonComplete(sku, lessonId, lessonTotal) {
    if (!sku || lessonId == null) return null;

    // 1. Update localStorage immediately (preserve existing shape).
    const existing = readSkuProgress(sku) || {
      completedLessons: [],
      lessonTotal: String(lessonTotal || ''),
    };
    const set = new Set(existing.completedLessons);
    set.add(String(lessonId));
    const merged = {
      ...existing,
      completedLessons: [...set],
      lessonTotal: String(lessonTotal || existing.lessonTotal || ''),
    };
    writeSkuProgress(sku, merged);

    // 2. Push to the worker. Worker expects lessonTotal as a number.
    return postProgress({
      sku,
      completedLessons: merged.completedLessons,
      lessonTotal: Number(merged.lessonTotal) || 0,
    });
  }

  // On every page load, push any localStorage progress to the worker. The
  // worker is idempotent (set-union merge), so calling this repeatedly is safe.
  async function syncLocalToServer() {
    const all = readAllProgress();
    const skus = Object.keys(all);
    if (skus.length === 0) return;

    await Promise.all(
      skus.map((sku) => {
        const entry = all[sku] || {};
        const completedLessons = Array.isArray(entry.completedLessons)
          ? entry.completedLessons
          : [];
        const lessonTotal = Number(entry.lessonTotal) || 0;
        if (completedLessons.length === 0 || !lessonTotal) return null;
        return postProgress({ sku, completedLessons, lessonTotal });
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // localStorage helpers
  // ---------------------------------------------------------------------------

  function readAllProgress() {
    const out = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(LOCAL_STORAGE_PREFIX)) continue;
      const sku = key.slice(LOCAL_STORAGE_PREFIX.length);
      try {
        const raw = localStorage.getItem(key);
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed && typeof parsed === 'object') out[sku] = parsed;
      } catch {
        // ignore invalid entries
      }
    }
    return out;
  }

  function readSkuProgress(sku) {
    try {
      const raw = localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${sku}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  function writeSkuProgress(sku, value) {
    try {
      localStorage.setItem(
        `${LOCAL_STORAGE_PREFIX}${sku}`,
        JSON.stringify(value),
      );
    } catch {
      // localStorage full / unavailable — ignore
    }
  }

  // ---------------------------------------------------------------------------
  // Worker call
  // ---------------------------------------------------------------------------

  async function postProgress({ sku, completedLessons, lessonTotal }) {
    try {
      const response = await fetch(`${PROXY_BASE}/lesson-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ sku, completedLessons, lessonTotal }),
      });
      if (!response.ok) {
        console.error(
          '[course-progress] sync failed',
          response.status,
          await response.text(),
        );
        return null;
      }
      return await response.json();
    } catch (err) {
      console.error('[course-progress] sync error', err);
      return null;
    }
  }

  // Expose for the section's player script.
  window.courseProgress = { markLessonComplete, syncLocalToServer };

  // Fire-and-forget on load.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncLocalToServer());
  } else {
    syncLocalToServer();
  }
})();
